'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { ToolsRow } from '@/components/content/tools-row'
import {
  DIFFICULTY_META,
  difficultyLabel,
  type DesignUsageCard,
  type RopeConsumptionRow,
  type RopeWrapRow,
} from './bench-types'

/** Stored difficulty values. Labels render via the same `filters.*` keys the
 *  library grids and tutorial reader use (`beginner` → "Easy"); only reached
 *  when DIFFICULTY_META knows the value, so the key always exists. */
type DifficultyKey = 'beginner' | 'intermediate' | 'advanced'

/* ── Tools row (bookmark · like · share) ───────────────────────────────── */

/**
 * Bookmark / like / share tools, using the shared <ToolsRow /> so the bench
 * flogger pages render the same 3-tile engagement widget as the tutorial and
 * scene detail pages. Rendered at the TOP of the reference column (above the
 * Overview), mirroring the tutorial reader's Tools rail. `contentId` is
 * pre-resolved on the server so the first toggle doesn't wait on a lookup.
 */
export function BenchToolsRow({
  contentId,
  userId,
  pageUrl,
  title,
}: {
  contentId: string | null
  userId: string | null
  pageUrl: string
  title: string
}) {
  return (
    <div className="mb-7 flex justify-center">
      <div className="w-full max-w-[300px]">
        <ToolsRow
          contentId={contentId}
          userId={userId}
          shareUrl={pageUrl}
          shareTitle={title}
        />
      </div>
    </div>
  )
}

/* ── Title bar ─────────────────────────────────────────────────────────── */

export function TitleBar({
  typeLabel,
  title,
  difficulty,
  isGated,
  right,
  formula,
}: {
  typeLabel: string
  title: string
  difficulty?: string
  isGated?: boolean
  right?: React.ReactNode
  formula?: React.ReactNode
}) {
  const t = useTranslations()
  const diff = difficulty ? DIFFICULTY_META[difficulty] : null
  return (
    <div className="w-full text-white" style={{ background: 'var(--ink)' }}>
      <div className="mx-auto flex max-w-[1280px] flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 py-[18px]">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
          {/* Breadcrumb trail — the "← Flogger Forge" link folded together with
              the component type. Full-width on every viewport, so it sits on its
              own line above the title on desktop and mobile alike (replaces the
              old desktop inline back-link + type badge). */}
          <nav
            aria-label={t('floggerBench.breadcrumbAria')}
            className="flex w-full min-w-0 items-center gap-1.5 text-[13px] text-white/70 sm:text-sm"
          >
            <Link
              href="/floggers"
              className="inline-flex shrink-0 items-center gap-1 transition-colors hover:text-white"
            >
              <span aria-hidden="true">←</span> {t('floggersList.title')}
            </Link>
            <span className="text-white/40" aria-hidden="true">
              /
            </span>
            <span className="truncate text-white/90">{typeLabel}</span>
          </nav>
          {/* The abbreviation is not shown as a separate chip — every
              component that has one already carries it in the title, e.g.
              "Footrope Knot (FRK)", so a chip would only duplicate it. */}
          <h1 className="text-[26px] font-bold leading-tight">{title}</h1>
          {diff && (
            <span
              className="text-[12px] font-bold uppercase"
              style={{ color: diff.onDark }}
            >
              {t(`filters.${difficulty as DifficultyKey}`)}
            </span>
          )}
          {isGated && (
            <span
              className="border px-[9px] py-[3px] text-[11px] font-medium uppercase tracking-[0.05em]"
              style={{
                color: '#FEF3C7',
                background: 'rgba(254,243,199,0.15)',
                borderColor: 'rgba(254,243,199,0.4)',
              }}
            >
              {t('filters.membersOnly')}
            </span>
          )}
        </div>
        {right && <div className="flex items-center">{right}</div>}
      </div>
      {formula && (
        <div
          className="border-t"
          style={{ borderColor: 'rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.3)' }}
        >
          <div className="mx-auto max-w-[1280px] px-6 py-2.5">{formula}</div>
        </div>
      )}
    </div>
  )
}

/* ── Resume button ─────────────────────────────────────────────────────── */

export function ResumeButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3.5 py-2 text-[13px] font-bold text-white transition-colors hover:brightness-110"
      style={{ background: 'var(--brand-500)' }}
    >
      {label} <span aria-hidden="true">→</span>
    </button>
  )
}

/* ── Notation line with a highlighted abbreviation ─────────────────────── */

export function NotationLine({
  notation,
  highlight,
  className,
}: {
  notation: string
  highlight?: string
  className?: string
}) {
  if (!highlight) {
    return <span className={className}>{notation}</span>
  }
  // Highlight the component's code AND its strand-count suffix as one token, so
  // "CS" lights up the whole "CS6" and "FRK" the whole "FRK6" — consistently,
  // digits included. The boundaries (no letter before, no letter after the
  // digits) keep it from lighting up a different code that merely starts the
  // same, e.g. "CS" inside "CSK" or "CSa4".
  const esc = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(?<![A-Za-z])${esc}\\d*(?![A-Za-z])`, 'g')
  const parts: React.ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = re.exec(notation)) !== null) {
    if (match.index > last) parts.push(notation.slice(last, match.index))
    parts.push(
      <span
        key={key++}
        className="px-1 font-bold"
        style={{ background: 'var(--brand-50)', color: 'var(--brand-500)' }}
      >
        {match[0]}
      </span>,
    )
    last = match.index + match[0].length
  }
  if (parts.length === 0) {
    return <span className={className}>{notation}</span>
  }
  if (last < notation.length) parts.push(notation.slice(last))
  return <span className={className}>{parts}</span>
}

/* ── Tip callout ───────────────────────────────────────────────────────── */

export function TipCallout({
  lead,
  children,
}: {
  lead: string
  children: React.ReactNode
}) {
  return (
    <div
      className="mt-6 border px-4 py-3 text-[14px] leading-relaxed"
      style={{ borderColor: 'var(--rope-light)', background: '#FDF6EF' }}
    >
      <span className="font-bold" style={{ color: 'var(--rope-deep)' }}>
        {lead}
      </span>{' '}
      <span style={{ color: '#374151' }}>{children}</span>
    </div>
  )
}

/* ── Gather-first checklist ────────────────────────────────────────────── */

export function GatherFirst({ items, title }: { items: string[]; title?: string }) {
  const t = useTranslations('floggerBench')
  const [done, setDone] = useState<Set<number>>(new Set())
  if (!items.length) return null
  return (
    <div className="mt-6 border px-4 py-3.5" style={{ borderColor: '#E2E4EC', background: '#FAFAFC' }}>
      <div
        className="mb-2 text-[11px] font-bold uppercase tracking-wider"
        style={{ color: 'var(--brand-700)' }}
      >
        {title ?? t('gatherFirst')}
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => {
          const checked = done.has(i)
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() =>
                  setDone((prev) => {
                    const next = new Set(prev)
                    if (next.has(i)) next.delete(i)
                    else next.add(i)
                    return next
                  })
                }
                className="flex w-full items-start gap-2.5 text-left text-[14px]"
                style={{ color: '#374151' }}
              >
                <span
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border"
                  style={{
                    borderColor: checked ? 'var(--brand-700)' : '#C4C7D4',
                    borderWidth: 1.5,
                    background: checked ? 'var(--brand-700)' : 'transparent',
                  }}
                >
                  {checked && (
                    <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="#fff" strokeWidth={2.5}>
                      <path d="M3 8l3.5 3.5L13 4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className={checked ? 'line-through opacity-60' : ''}>{item}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/* ── Rope consumption table (knots) ───────────────────────────────────── */

export function RopeConsumptionTable({
  rows,
  activeVariant,
}: {
  rows: RopeConsumptionRow[]
  activeVariant?: string
}) {
  const t = useTranslations('floggerBench')
  if (!rows.length) return null
  const fmt = (inch?: number, cm?: number) =>
    inch != null ? `${inch}"${cm != null ? ` (${cm} cm)` : ''}` : '—'
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full border text-[13.5px]" style={{ borderColor: '#E2E4EC' }}>
        <thead>
          <tr className="text-left" style={{ color: '#5F647A' }}>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>
              {t('variant')}
            </th>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>
              {t('ropePerStrand')}
            </th>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>
              {t('height')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const active =
              activeVariant &&
              (r.variant === activeVariant || r.abbreviation === activeVariant)
            return (
              <tr
                key={i}
                style={{ background: active ? 'var(--brand-50)' : 'transparent' }}
              >
                <td
                  className="border-b px-3 py-2 font-bold"
                  style={{ borderColor: '#EEEFF4', color: 'var(--brand-700)' }}
                >
                  {r.variant || r.abbreviation || '—'}
                </td>
                <td
                  className="border-b px-3 py-2 tabular-nums"
                  style={{ borderColor: '#EEEFF4', color: '#374151' }}
                >
                  {fmt(r.rope_consumed_in, r.rope_consumed_cm)}
                </td>
                <td
                  className="border-b px-3 py-2 tabular-nums"
                  style={{ borderColor: '#EEEFF4', color: '#374151' }}
                >
                  {fmt(r.linear_distance_in, r.linear_distance_cm)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ── Rope wrap table (handles) ────────────────────────────────────────── */

export function RopeWrapTable({ rows }: { rows: RopeWrapRow[] }) {
  const t = useTranslations('floggerBench')
  if (!rows.length) return null
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full border text-[13.5px]" style={{ borderColor: '#E2E4EC' }}>
        <thead>
          <tr className="text-left" style={{ color: '#5F647A' }}>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>Ø</th>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>{t('ropePerInch')}</th>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>{t('wrapsPerInch')}</th>
            <th className="border-b px-3 py-2 font-bold" style={{ borderColor: '#d1d5db' }}>{t('toCover')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="border-b px-3 py-2 font-bold" style={{ borderColor: '#EEEFF4', color: 'var(--brand-700)' }}>
                {r.rope_diameter || '—'}
              </td>
              <td className="border-b px-3 py-2 tabular-nums" style={{ borderColor: '#EEEFF4', color: '#374151' }}>
                {r.rope_per_unit || '—'}
              </td>
              <td className="border-b px-3 py-2 tabular-nums" style={{ borderColor: '#EEEFF4', color: '#374151' }}>
                {r.wraps_per_inch || '—'}
              </td>
              <td className="border-b px-3 py-2 tabular-nums" style={{ borderColor: '#EEEFF4', color: '#374151' }}>
                {r.amount_to_cover || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ── "Where you'll tie it" / "Needed for" derived section ──────────────── */

export function WhereYoullTieIt({
  cards,
  abbreviation,
  heading,
}: {
  cards: DesignUsageCard[]
  abbreviation?: string
  heading: string
}) {
  // One translator for the whole card list (not per card).
  const t = useTranslations()
  if (!cards.length) return null
  return (
    <section className="border-t px-6 pb-12 pt-10" style={{ borderColor: '#EEEFF4' }}>
      <div className="mx-auto max-w-[1280px]">
        <h2 className="text-[22px] font-bold" style={{ color: '#111827' }}>
          {heading}
        </h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          {cards.map((c) => {
            const diff = c.difficulty ? DIFFICULTY_META[c.difficulty] : null
            return (
              <Link
                key={c.id}
                href={`/floggers/${c.slug}`}
                className="block border p-4 transition-colors hover:border-[color:var(--brand-200)]"
                style={{ borderColor: '#E2E4EC', background: '#fff' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-[16px] font-bold" style={{ color: '#111827' }}>
                    {c.title}
                  </span>
                  {c.isGated && (
                    <span
                      className="shrink-0 border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: '#92400E', background: '#FEF3C7', borderColor: '#FDE68A' }}
                    >
                      {t('filters.members')}
                    </span>
                  )}
                </div>
                {/* Difficulty sits directly under the card title. */}
                {diff && (
                  <div className="mt-1 text-[11px] font-bold uppercase" style={{ color: diff.onLight }}>
                    {t(`filters.${c.difficulty as DifficultyKey}`)}
                  </div>
                )}
                {c.notation && (
                  <div className="mt-2 font-mono text-[13px]" style={{ color: '#5F647A' }}>
                    <NotationLine notation={c.notation} highlight={abbreviation} />
                  </div>
                )}
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export { difficultyLabel }
